import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authApi } from "../api/endpoints/auth";
import { tokenStorage } from "./tokenStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => tokenStorage.getAccess());
    const [isLoading, setIsLoading] = useState(Boolean(tokenStorage.getAccess()));
    const [error, setError] = useState(null);

    // Validate the existing token on mount.
    useEffect(() => {
        let cancelled = false;
        async function bootstrap() {
            const existing = tokenStorage.getAccess();
            if (!existing) {
                setIsLoading(false);
                return;
            }
            try {
                const me = await authApi.me();
                if (!cancelled) setUser(me);
            } catch (e) {
                // 401 interceptor already cleared the token.
                if (!cancelled) setUser(null);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }
        bootstrap();
        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback(async (email, password) => {
        setError(null);
        try {
            const data = await authApi.login({ email, password });
            tokenStorage.setAccess(data.accessToken);
            tokenStorage.setRefresh(data.refreshToken);
            setToken(data.accessToken);
            setUser(data.user);
            return data.user;
        } catch (e) {
            const message =
                e?.response?.data?.detail ||
                e?.response?.data?.message ||
                e?.message ||
                "Login failed";
            setError(message);
            throw e;
        }
    }, []);

    const logout = useCallback(() => {
        tokenStorage.clear();
        setUser(null);
        setToken(null);
        setError(null);
    }, []);

    const value = {
        user,
        token,
        isAuthenticated: Boolean(user && token),
        isLoading,
        error,
        login,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within an <AuthProvider>");
    }
    return ctx;
}
